import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LogoutOutlined, SwapOutlined } from '@ant-design/icons';

import starlingMark from '@/assets/starling-mark.svg';

// ─────────────────────────────────────────────────────────────
// Единый сайдбар админки (десктоп) по снимку design/admin/app/Sidebar.jsx
// и брифу docs/design/20260821-design-brief-sidebar.md.
//
// Иерархия сверху вниз: бренд (приглушённый) → имя контекста (акцент) →
// навигация → карточка пользователя. Один паттерн, два контекста:
// tenant (кабинет оптики) и platform (владелец платформы).
//
// Компонент рендерится ВНУТРИ AntD <Sider> — тёмный фон и адаптив (breakpoint
// md, collapsedWidth 0) остаются на стороне Sider. Здесь — только иерархия/вид.
// ─────────────────────────────────────────────────────────────

export interface SidebarNavItem {
  /** Ключ подсветки — совпадает с selectedKey (префиксная логика в layout). */
  key: string;
  to: string;
  label: string;
  icon: ReactNode;
}

export interface SidebarNavSection {
  /** uppercase/muted заголовок секции; если пусто — не рендерится. */
  label?: string;
  items: SidebarNavItem[];
}

interface AppSidebarProps {
  /**
   * Название бренда в полосе вверху. Кабинет оптики — «Starling Light»
   * (дефолт); консоль платформы — «Starling»: платформа уже не только Light
   * (решение пользователя 2026-09-11).
   */
  brandTitle?: string;
  /** Подзаголовок бренда: «Админка оптики» / «Платформа». */
  brandSubtitle: string;
  /** Главный акцент: название оптики / «Платформа». */
  contextName: string;
  /**
   * Показывать под именем ссылку «Сменить оптику» (только при 2+ контекстах).
   * Требует `switchContextLabel` + `onSwitchContext`; иначе ссылки нет.
   */
  canSwitchContext?: boolean;
  /** Подпись ссылки смены оптики (i18n). */
  switchContextLabel?: string;
  /** Обработчик клика по «Сменить оптику» — навигация на switch-route. */
  onSwitchContext?: () => void;
  /** Основная секция навигации (прижата к верху). */
  mainSection: SidebarNavSection;
  /** Нижняя секция (напр. «Оптика» → Настройки); опущена для платформы. */
  tailSection?: SidebarNavSection;
  /** Ключ активного пункта (по префиксу пути — считается в layout). */
  selectedKey: string;
  /** Первая строка юзер-карточки: имя пользователя или роль (фолбэк). */
  userPrimary: string;
  /** Вторая строка: роль (если есть имя) либо пусто. */
  userSecondary?: string;
  logoutLabel: string;
  onLogout: () => void;
}

// Цвета тёмного сайдбара (из снимка, dark-ветка).
const FG = 'rgba(255,255,255,0.78)';
const FG_ACTIVE = 'var(--health-turquoise-200)';
const BG_ACTIVE = 'rgba(45,189,184,0.18)';
const BG_HOVER = 'rgba(255,255,255,0.05)';
const LABEL_MUTED = 'rgba(255,255,255,0.42)';
const HAIRLINE = 'rgba(255,255,255,0.08)';

const sectionLabelStyle: React.CSSProperties = {
  font: '600 9px/1 var(--font-sans)',
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: LABEL_MUTED,
  padding: '10px 10px 6px',
};

export function AppSidebar(props: AppSidebarProps): JSX.Element {
  const {
    brandTitle = 'Starling Light',
    brandSubtitle,
    contextName,
    canSwitchContext,
    switchContextLabel,
    onSwitchContext,
    mainSection,
    tailSection,
    selectedKey,
    userPrimary,
    userSecondary,
    logoutLabel,
    onLogout,
  } = props;

  return (
    <div
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--deep-trust-blue)',
      }}
    >
      <BrandBand title={brandTitle} subtitle={brandSubtitle} />

      <div
        style={{
          padding: '0 12px 16px',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <ContextName
          name={contextName}
          switchLabel={canSwitchContext && onSwitchContext ? switchContextLabel : undefined}
          onSwitch={onSwitchContext}
        />

        {mainSection.label && <div style={sectionLabelStyle}>{mainSection.label}</div>}
        {mainSection.items.map((item) => (
          <NavItem key={item.key} item={item} active={selectedKey === item.key} />
        ))}

        {/* Spacer — прижимает нижнюю секцию и юзер-карточку к низу. */}
        <div style={{ flex: 1, minHeight: 12 }} />

        {tailSection && tailSection.items.length > 0 && (
          <>
            {tailSection.label && <div style={sectionLabelStyle}>{tailSection.label}</div>}
            {tailSection.items.map((item) => (
              <NavItem key={item.key} item={item} active={selectedKey === item.key} />
            ))}
          </>
        )}

        <UserCard
          primary={userPrimary}
          secondary={userSecondary}
          logoutLabel={logoutLabel}
          onLogout={onLogout}
        />
      </div>
    </div>
  );
}

// ── Бренд-платформы: приглушённая полоса вверху (знак-скворец + название бренда).
function BrandBand({ title, subtitle }: { title: string; subtitle: string }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '11px 16px',
        background: 'rgba(0,0,0,0.22)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
      }}
    >
      <img
        src={starlingMark}
        alt=""
        style={{ width: 18, height: 18, flexShrink: 0, display: 'block', opacity: 0.85 }}
      />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            font: '600 11px/1.2 var(--font-sans)',
            letterSpacing: '0.04em',
            color: 'rgba(255,255,255,0.52)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </div>
        <div
          style={{
            font: '500 9px/1.3 var(--font-sans)',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.35)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {subtitle}
        </div>
      </div>
    </div>
  );
}

// ── Имя контекста: название оптики или «Платформа» — главный ориентир.
// При 2+ контекстах под именем — мелкая ссылка «Сменить оптику» (M7.2c).
function ContextName({
  name,
  switchLabel,
  onSwitch,
}: {
  name: string;
  switchLabel?: string;
  onSwitch?: () => void;
}): JSX.Element {
  const [hover, setHover] = useState(false);
  return (
    <div style={{ padding: '18px 10px 8px' }}>
      <div
        title={name}
        style={{
          font: '800 18px/1.25 var(--font-sans)',
          letterSpacing: '-0.015em',
          color: '#fff',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {name}
      </div>
      {switchLabel && onSwitch && (
        <button
          type="button"
          onClick={onSwitch}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            marginTop: 6,
            padding: 0,
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            font: '600 11px/1.2 var(--font-sans)',
            color: hover ? FG_ACTIVE : 'rgba(255,255,255,0.55)',
            transition: 'color var(--dur-fast) var(--ease-out)',
          }}
        >
          <SwapOutlined style={{ fontSize: 11 }} />
          <span>{switchLabel}</span>
        </button>
      )}
    </div>
  );
}

// ── Пункт навигации: React-Router Link, стилизованный как кнопка снимка.
function NavItem({ item, active }: { item: SidebarNavItem; active: boolean }): JSX.Element {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={item.to}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        background: active ? BG_ACTIVE : hover ? BG_HOVER : 'transparent',
        color: active ? FG_ACTIVE : FG,
        borderRadius: 10,
        width: '100%',
        font: active ? '700 13px/1 var(--font-sans)' : '600 13px/1 var(--font-sans)',
        textAlign: 'left',
        position: 'relative',
        transition:
          'background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)',
      }}
    >
      {active && (
        <span
          style={{
            position: 'absolute',
            left: -12,
            top: 8,
            bottom: 8,
            width: 3,
            borderRadius: '0 3px 3px 0',
            background: 'var(--accent)',
          }}
        />
      )}
      <span style={{ flexShrink: 0, fontSize: 18, display: 'inline-flex' }}>{item.icon}</span>
      <span style={{ flex: 1 }}>{item.label}</span>
    </Link>
  );
}

// ── Карточка пользователя (низ, оба контекста): аватар + имя/роль + «Выйти».
function UserCard({
  primary,
  secondary,
  logoutLabel,
  onLogout,
}: {
  primary: string;
  secondary?: string;
  logoutLabel: string;
  onLogout: () => void;
}): JSX.Element {
  const initial = (primary || '?').trim().charAt(0).toUpperCase();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        marginTop: 14,
        padding: '14px 0 2px',
        borderTop: `1px solid ${HAIRLINE}`,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          flexShrink: 0,
          background: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.16)',
          color: '#fff',
          font: '700 11px/1 var(--font-sans)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {initial}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          title={primary}
          style={{
            font: '600 12px/1.25 var(--font-sans)',
            color: '#fff',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {primary}
        </div>
        {secondary && (
          <div
            title={secondary}
            style={{
              font: '500 11px/1.25 var(--font-sans)',
              marginTop: 3,
              color: 'rgba(255,255,255,0.62)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {secondary}
          </div>
        )}
      </div>
      <button
        type="button"
        title={logoutLabel}
        aria-label={logoutLabel}
        onClick={onLogout}
        style={{
          width: 24,
          height: 24,
          borderRadius: 7,
          border: 0,
          flexShrink: 0,
          background: 'transparent',
          cursor: 'pointer',
          color: 'rgba(255,255,255,0.7)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LogoutOutlined style={{ fontSize: 15 }} />
      </button>
    </div>
  );
}
